import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND_URL}/api` });

const inp = "w-full border border-rule px-3 py-2 text-sm focus:outline-none focus:border-klein bg-white";
const labelCls = "block font-mono text-[10px] uppercase tracking-wider text-quiet mb-1";

const Slider = ({ label, value, onChange, testId }) => (
  <div className="mb-3">
    <div className="flex justify-between text-sm mb-1">
      <span className="text-ink">{label}</span>
      <span className="font-mono text-klein">{value}/5</span>
    </div>
    <input
      data-testid={testId}
      type="range"
      min={1}
      max={5}
      step={1}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full accent-klein"
    />
  </div>
);

const Pill = ({ active, onClick, children, testId }) => (
  <button
    data-testid={testId}
    type="button"
    onClick={onClick}
    className={`px-3 py-2 border font-mono text-xs uppercase ${
      active ? "bg-ink text-white border-ink" : "border-rule text-quiet hover:border-ink"
    }`}
  >
    {children}
  </button>
);

export default function PublicFeedback() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    submitter_name: "",
    submitter_email: "",
    communication: 3,
    role_fit: 3,
    stability: 3,
    technical_fit: 3,
    culture_fit: 3,
    technical_depth: 3,
    leadership: 3,
    behavioral_fit: 3,
    salary_fit: 3,
    strengths: "",
    concerns: "",
    recommendation: "proceed",
    overall: "select",
    comments: "",
    // R1 only
    ctc_current_monthly: "",
    ctc_expected_monthly: "",
    notice_period: "",
    marital_status: "",
    current_location: "",
    hometown: "",
    relocation: "",
    career_gap: "",
    career_gap_reason: "",
    asset_allocation: "",
    job_change_reason: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/public/feedback/${token}`);
        setCtx(r.data);
        if (r.data.already_submitted) setDone(true);
      } catch (e) {
        setErr(e?.response?.data?.detail || "This feedback link is invalid or has expired.");
      }
    })();
  }, [token]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.comments.trim()) return toast.error("Comments are mandatory");
    if (ctx.round === "round1" && form.career_gap === "yes" && !form.career_gap_reason.trim()) {
      return toast.error("Career gap reason is required");
    }
    const payload = {
      submitter_name: form.submitter_name,
      submitter_email: form.submitter_email,
      strengths: form.strengths,
      concerns: form.concerns,
      comments: form.comments,
    };
    if (ctx.round === "round1") {
      Object.assign(payload, {
        communication: form.communication, role_fit: form.role_fit, stability: form.stability,
        recommendation: form.recommendation,
        ctc_current_monthly: form.ctc_current_monthly === "" ? null : Number(form.ctc_current_monthly),
        ctc_expected_monthly: form.ctc_expected_monthly === "" ? null : Number(form.ctc_expected_monthly),
        notice_period: form.notice_period || null,
        marital_status: form.marital_status || null,
        current_location: form.current_location, hometown: form.hometown,
        relocation: form.relocation || null,
        career_gap: form.career_gap === "" ? null : form.career_gap === "yes",
        career_gap_reason: form.career_gap_reason,
        asset_allocation: form.asset_allocation === "" ? null : form.asset_allocation === "yes",
        job_change_reason: form.job_change_reason,
      });
    } else if (ctx.round === "round2") {
      Object.assign(payload, {
        technical_fit: form.technical_fit, communication: form.communication, culture_fit: form.culture_fit,
        recommendation: form.recommendation,
      });
    } else {
      Object.assign(payload, {
        technical_depth: form.technical_depth, leadership: form.leadership,
        behavioral_fit: form.behavioral_fit, salary_fit: form.salary_fit,
        overall: form.overall,
      });
    }
    setBusy(true);
    try {
      await api.post(`/public/feedback/${token}`, payload);
      setDone(true);
      toast.success("Feedback submitted — thank you!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  if (err) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-8">
        <div className="bg-white border border-rule p-8 max-w-lg text-center" data-testid="feedback-error">
          <h1 className="font-serif text-3xl text-ink">Link unavailable</h1>
          <p className="mt-3 text-sm text-quiet">{err}</p>
        </div>
      </div>
    );
  }
  if (!ctx) return <div className="p-8 text-quiet">Loading…</div>;
  if (done) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center p-8">
        <div className="bg-white border border-rule p-8 max-w-lg text-center" data-testid="feedback-done">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">Submitted</div>
          <h1 className="font-serif text-4xl text-ink mt-3">Thank you.</h1>
          <p className="mt-4 text-sm text-quiet">
            Your feedback for {ctx.candidate.full_name} ({ctx.round_label}) has been recorded.
            HR will follow up with next steps.
          </p>
        </div>
      </div>
    );
  }

  const isR1 = ctx.round === "round1";
  const isR2 = ctx.round === "round2";
  const isFinal = ctx.round === "final";

  return (
    <div className="min-h-screen bg-paper py-10 px-4">
      <Toaster position="top-right" />
      <div className="max-w-3xl mx-auto bg-white border border-rule" data-testid="public-feedback-page">
        <div className="p-8 border-b border-rule">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">Interview feedback · {ctx.round_label}</div>
          <h1 className="font-serif text-4xl text-ink mt-2">{ctx.candidate.full_name}</h1>
          <p className="text-sm text-quiet mt-2">
            {ctx.candidate.requisition_title} · {ctx.candidate.requisition_code}
          </p>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-4 text-xs text-quiet">
            <div>Email: <span className="text-ink">{ctx.candidate.email}</span></div>
            <div>Total exp: <span className="text-ink">{ctx.candidate.total_experience} yrs</span></div>
            <div>Current company: <span className="text-ink">{ctx.candidate.current_company || "—"}</span></div>
            <div>Location: <span className="text-ink">{ctx.candidate.current_location || "—"}</span></div>
            <div>Skills: <span className="text-ink">{ctx.candidate.skills || "—"}</span></div>
            <div>Scheduled: <span className="text-ink">{ctx.scheduled_at ? new Date(ctx.scheduled_at).toLocaleString() : "—"}</span></div>
          </div>
        </div>

        <div className="p-8 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className={labelCls}>Your name</div>
              <input data-testid="submitter-name" value={form.submitter_name} onChange={set("submitter_name")} placeholder="e.g. Priya Manager" className={inp} />
            </div>
            <div>
              <div className={labelCls}>Your email</div>
              <input data-testid="submitter-email" type="email" value={form.submitter_email} onChange={set("submitter_email")} placeholder="you@company.com" className={inp} />
            </div>
          </div>

          {isR1 && (
            <>
              <Slider label="Communication" value={form.communication} onChange={(v) => setForm({ ...form, communication: v })} testId="r1-communication" />
              <Slider label="Role fit" value={form.role_fit} onChange={(v) => setForm({ ...form, role_fit: v })} testId="r1-role-fit" />
              <Slider label="Stability" value={form.stability} onChange={(v) => setForm({ ...form, stability: v })} testId="r1-stability" />

              <div className="pt-4 mt-2 border-t border-rule">
                <div className="font-mono text-[10px] uppercase tracking-wider text-quiet mb-2">Data capture</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><div className={labelCls}>CTC current (monthly)</div><input data-testid="r1-ctc-current" type="number" min={0} value={form.ctc_current_monthly} onChange={set("ctc_current_monthly")} className={inp} /></div>
                  <div><div className={labelCls}>CTC expected (monthly)</div><input data-testid="r1-ctc-expected" type="number" min={0} value={form.ctc_expected_monthly} onChange={set("ctc_expected_monthly")} className={inp} /></div>
                  <div><div className={labelCls}>Notice period</div>
                    <select data-testid="r1-notice" value={form.notice_period} onChange={set("notice_period")} className={inp}>
                      <option value="">—</option><option value="90_days">90 days</option><option value="60_days">60 days</option><option value="45_days">45 days</option><option value="30_days">30 days</option><option value="negotiable">Negotiable</option><option value="serving">Serving notice</option><option value="not_required">Not required</option>
                    </select>
                  </div>
                  <div><div className={labelCls}>Marital status</div>
                    <select data-testid="r1-marital" value={form.marital_status} onChange={set("marital_status")} className={inp}><option value="">—</option><option value="married">Married</option><option value="unmarried">Unmarried</option></select>
                  </div>
                  <div><div className={labelCls}>Current location</div><input data-testid="r1-location" value={form.current_location} onChange={set("current_location")} className={inp} /></div>
                  <div><div className={labelCls}>Hometown</div><input data-testid="r1-hometown" value={form.hometown} onChange={set("hometown")} className={inp} /></div>
                  <div><div className={labelCls}>Relocation</div>
                    <select data-testid="r1-relocation" value={form.relocation} onChange={set("relocation")} className={inp}><option value="">—</option><option value="open">Open</option><option value="not_open">Not open</option><option value="not_required">Not required</option></select>
                  </div>
                  <div><div className={labelCls}>Career gap?</div>
                    <select data-testid="r1-gap" value={form.career_gap} onChange={set("career_gap")} className={inp}><option value="">—</option><option value="yes">Yes</option><option value="no">No</option></select>
                  </div>
                </div>
                {form.career_gap === "yes" && (
                  <div className="mt-3"><div className={labelCls}>Career gap reason *</div><textarea data-testid="r1-gap-reason" rows={2} value={form.career_gap_reason} onChange={set("career_gap_reason")} className={inp} /></div>
                )}
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div><div className={labelCls}>Asset allocation (laptop/IT)?</div>
                    <select data-testid="r1-asset" value={form.asset_allocation} onChange={set("asset_allocation")} className={inp}><option value="">—</option><option value="yes">Yes</option><option value="no">No</option></select>
                  </div>
                </div>
                <div className="mt-3"><div className={labelCls}>Reason for job change</div><textarea data-testid="r1-job-reason" rows={2} value={form.job_change_reason} onChange={set("job_change_reason")} className={inp} /></div>
              </div>
            </>
          )}

          {isR2 && (
            <>
              <Slider label="Technical fit" value={form.technical_fit} onChange={(v) => setForm({ ...form, technical_fit: v })} testId="r2-tech-fit" />
              <Slider label="Communication" value={form.communication} onChange={(v) => setForm({ ...form, communication: v })} testId="r2-communication" />
              <Slider label="Culture fit" value={form.culture_fit} onChange={(v) => setForm({ ...form, culture_fit: v })} testId="r2-culture-fit" />
            </>
          )}

          {isFinal && (
            <>
              <Slider label="Technical depth" value={form.technical_depth} onChange={(v) => setForm({ ...form, technical_depth: v })} testId="f-tech" />
              <Slider label="Leadership" value={form.leadership} onChange={(v) => setForm({ ...form, leadership: v })} testId="f-lead" />
              <Slider label="Behavioral fit" value={form.behavioral_fit} onChange={(v) => setForm({ ...form, behavioral_fit: v })} testId="f-behavior" />
              <Slider label="Salary fit" value={form.salary_fit} onChange={(v) => setForm({ ...form, salary_fit: v })} testId="f-salary" />
            </>
          )}

          <div>
            <div className={labelCls}>Strengths</div>
            <textarea data-testid="strengths" rows={2} value={form.strengths} onChange={set("strengths")} className={inp} />
          </div>
          <div>
            <div className={labelCls}>Concerns</div>
            <textarea data-testid="concerns" rows={2} value={form.concerns} onChange={set("concerns")} className={inp} />
          </div>

          {(isR1 || isR2) && (
            <div>
              <div className={labelCls}>Recommendation</div>
              <div className="grid grid-cols-3 gap-2">
                {["reject", "hold", "proceed"].map((r) => (
                  <Pill key={r} active={form.recommendation === r} onClick={() => setForm({ ...form, recommendation: r })} testId={`rec-${r}`}>
                    {r}
                  </Pill>
                ))}
              </div>
            </div>
          )}

          {isFinal && (
            <div>
              <div className={labelCls}>Overall</div>
              <div className="grid grid-cols-3 gap-2">
                {["select", "hold", "reject"].map((r) => (
                  <Pill key={r} active={form.overall === r} onClick={() => setForm({ ...form, overall: r })} testId={`overall-${r}`}>
                    {r}
                  </Pill>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className={labelCls}>Comments (mandatory)</div>
            <textarea data-testid="comments" rows={4} value={form.comments} onChange={set("comments")} className={inp} />
          </div>

          <div className="pt-4 border-t border-rule">
            <button
              data-testid="submit-feedback"
              onClick={submit}
              disabled={busy || !form.comments.trim()}
              className="w-full bg-klein hover:bg-klein-hover text-white py-3 text-sm uppercase tracking-wider disabled:opacity-50"
            >
              {busy ? "Submitting…" : "Submit feedback"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
